import React, { useCallback, useRef } from 'react';
import { t } from '@lingui/macro';

import Breadcrumbs from '@rover/components/src/navigation/Breadcrumbs';
import { Box } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import Accordion from '@rover/react-lib/src/components/lists/Accordion/Accordion';
import AccordionItem from '@rover/react-lib/src/components/lists/Accordion/AccordionItem';
import FooterLinkList from '@rover/react-lib/src/pages/shared/FooterLinks/FooterLinkList';
import type { Breadcrumb } from '@rover/rsdk/src/apiClient/latest/model/breadcrumb';
import type { RelatedServices } from '@rover/rsdk/src/apiClient/latest/model/relatedServices';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import getBrandDisplayName from '@rover/shared/js/utils/getBrandDisplayName';
import type { LinkedLocation } from '@rover/types/src/apiResponses/FetchSeoLocationResponse';

import {
  FooterAccordionSection,
  FooterLinksAccordionToggle,
} from '../../utilities/analyticsEvents';
import { PageSectionId, useSectionRef } from '../hooks/useSectionRef';

export type SeoLocationFooterLinksProps = {
  breadcrumbData: Breadcrumb[];
  serviceName: string;
  locationName: string;
  linkedLocations: LinkedLocation[];
  relatedServices: RelatedServices[];
  internalServiceTypeSlug: string;
};

const SeoLocationFooterLinks = ({
  breadcrumbData,
  serviceName,
  locationName,
  linkedLocations,
  relatedServices,
  internalServiceTypeSlug,
}: SeoLocationFooterLinksProps): JSX.Element => {
  const brandDisplayName = getBrandDisplayName();

  const nearbyLocationsAccordionLabel = t`${brandDisplayName} also offers ${serviceName} in:`;
  const otherServicesAccordionLabel = t`${brandDisplayName} also offers other services in ${locationName}`;
  const ref = useSectionRef(PageSectionId.Footer);
  const nearbyLocationsLinks = linkedLocations.map((ll) => ({
    name: ll.locationName,
    url: ll.absoluteUrl,
  }));
  const otherServicesLinks = relatedServices.map((rs) => ({
    name: rs.internalLinkingTitle,
    url: rs.url,
  }));
  const showNearbyLocations = nearbyLocationsLinks.length > 0;
  const showOtherServices = otherServicesLinks.length > 0;
  const showAccordion = showNearbyLocations || showOtherServices;

  const emittedSections = useRef(new Set<FooterAccordionSection>());

  const handleToggle = useCallback(
    (accordionSection: FooterAccordionSection) => {
      if (emittedSections.current.has(accordionSection)) return;
      emittedSections.current.add(accordionSection);
      emitAnalyticsEvent(
        new FooterLinksAccordionToggle({
          internalServiceTypeSlug,
          accordionSection,
        })
      );
    },
    [internalServiceTypeSlug]
  );

  return (
    <Section background="contrast" py="12x" htmlRef={ref}>
      {breadcrumbData.length > 0 && (
        <Box mb="4x">
          <Breadcrumbs
            breadcrumbData={breadcrumbData}
            color="white"
            underline
            lastItemIsCurrentPage
          />
        </Box>
      )}

      {showAccordion && (
        <Accordion itemColorVariant="primary_inverse" headingSize="200">
          {showNearbyLocations && (
            <AccordionItem
              itemHeading={nearbyLocationsAccordionLabel}
              itemBody={<FooterLinkList linkList={nearbyLocationsLinks} numColumns={[1, 2]} />}
              onToggle={() => handleToggle(FooterAccordionSection.NEARBY_LOCATIONS)}
            />
          )}
          {showOtherServices && (
            <AccordionItem
              itemHeading={otherServicesAccordionLabel}
              itemBody={<FooterLinkList linkList={otherServicesLinks} numColumns={[1, 2]} />}
              onToggle={() => handleToggle(FooterAccordionSection.OTHER_SERVICES)}
            />
          )}
        </Accordion>
      )}
    </Section>
  );
};

export default SeoLocationFooterLinks;
